import { useMemo, useState, type ReactNode } from 'react';
import { Box, Typography } from '@mui/material';

import type { DistrictStats } from '../districtStatsApi';
import { chartModel, type ChartView } from '../districtStatsChart';
import { tileRows, type Tile } from '../districtStatsTiles';
import { CARD_SX, DASH } from '../tokens';
import { ConnectSisBanner } from './ConnectSisBanner';
import { StudentsByGradeChart } from './StudentsByGradeChart';

export function StatTile({ tile }: { tile: Tile }) {
  return (
    <Box
      data-testid="stat-tile"
      sx={{
        flex: '1 1 0',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        padding: '8px 16px',
        bgcolor: DASH.stats.tileBg,
        borderRadius: '8px',
      }}
    >
      <Typography component="span" sx={{ fontSize: 32, lineHeight: '40px', fontWeight: 800, letterSpacing: '-0.5px', color: DASH.ink }}>
        {tile.value}
      </Typography>
      <Typography component="span" sx={{ fontSize: 16, lineHeight: '24px', fontWeight: 500, color: DASH.muted }}>
        {tile.label}
      </Typography>
    </Box>
  );
}

export function CardHead({ districtName }: { districtName: string }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      <Typography component="span" sx={{ fontSize: 16, lineHeight: '24px', fontWeight: 700, color: DASH.muted }}>
        English Language Learning Program
      </Typography>
      <Typography component="h2" sx={{ fontSize: 28, lineHeight: '36px', fontWeight: 600, color: DASH.ink }}>
        {districtName}
      </Typography>
    </Box>
  );
}

export function DistrictStatsCard({
  stats,
  now = new Date(),
  checklist,
}: {
  stats: DistrictStats;
  now?: Date;
  checklist?: ReactNode;
}) {
  const [view, setView] = useState<ChartView>('ml');
  const model = useMemo(() => chartModel(stats, view), [stats, view]);
  const rows = useMemo(() => tileRows(stats, now), [stats, now]);
  return (
    <Box
      component="section"
      role="region"
      aria-label="District stats"
      sx={{ ...CARD_SX, display: 'flex', flexDirection: 'column', gap: '20px' }}
    >
      {checklist !== undefined ? checklist : stats.flashlightConnected && !stats.sisConnected ? <ConnectSisBanner /> : null}
      <CardHead districtName={stats.districtName} />
      {rows.map((row, index) => (
        <Box key={index} data-testid="stat-tile-row" sx={{ display: 'flex', gap: '20px' }}>
          {row.map((tile) => (
            <StatTile key={tile.label} tile={tile} />
          ))}
        </Box>
      ))}
      <StudentsByGradeChart model={model} view={view} onViewChange={setView} />
    </Box>
  );
}
